/**
 * @file        auth-context.js - AuthContext class for dcp.identity module
 * @author      Wes Garland
 * @date        Aug 2024
 */
'use strict';

const jnu = require('dcp/dcp-client/jnu');
const operations = require('dcp/pxa/operations');

const { assert }        = require('dcp/common/dcp-assert');
const { KVIN }          = require('./index');
const { DCPError }      = require('dcp/common/dcp-error');
const { Address }       = require('dcp/dcp-client/wallet');
const { localStorage,
        sessionStorage,
      }                 = require('./ls-storage.js');
/**
 * An AuthContext lives only in a d9ve origin and represents an account that has been logged into
 * with the authenticate.html page. The keystore associated with the AuthContext could be a "root"
 * key that has never been proxied (could happen with an uploaded keystore), or it could be a
 * high-permissions (usually $login) key given to us, usually by oAuth.
 *
 * All sessions that derive from this context proxy to the key that is stored with the context, and
 * the keys giving these sessions come from authorize.html, as it applies to new-px for a new key
 * proxy the context key, generally with reduced access.
 *
 * Everything this browser "knows" about a given DCP identity is stored in a context. Contexts are
 * always persistent, although the keystore associated with the context could expire. This means, for
 * example, that a context might know a user's email address or name, but the user would still have
 * to go through an authentication cycle ('enter your password again') before an authorization cycle
 * could begin.
 *
 * Each context has an identifier and an authAddress. It is possible that more than one identifier
 * points to the same authAddress; this is why we store and identifier->authAddress pointer in
 * localStorage that is separate for the context; context are identified by authAddress.
 */
exports.AuthContext = class AuthContext
{
  #identifier;    /* identifier for this context - oAuth email, proxyAddress - used to find authAddress in localStorage */
  #authAddress;   /* unique identifier for this context - used to find keystore in localStorage */
  /**
   * Create an AuthContext.
   * @param {any}              persist    truey if the context should be persisted in localStorage;
   *                                      falsey if not. If persist is not false, it will become
   *                                      true if the address corresponds to a $login proxy that is
   *                                      owned by the authAddress.
   */
  constructor(identifier, persist)
  {
    assert(window.location.origin === dcpConfig.pxAuth.location.origin);
    if (persist !== false)
      persist = true;
    this.persist = persist;
    this.#identifier = identifier;
  }

  get authAddress()
  {
    return this.#authAddress;
  }

  get identifier()
  {
    return this.#identifier;
  }

  /**
   * Populate an AuthContext, given a keystore. If the keystore is a $login-level keystore, it is
   * de-proxied via Jnu to discover authAddress. Otherwise, it is treated as an unproxied keystore.
   */
  async populate(keystore, more)
  {
    assert(keystore);
    if (this.keystore)
      throw new Error('auth context already populated');
    Object.assign(more, this);
    this.keystore = keystore;

    /* Pull the authAddress/owner out of the db when the authentication is via a $login proxy, otherwise
     * proceed as if is an unproxied keystore login.
     */
    const db = await jnu.connect('pxAuth', keystore);
    const cursor = await db.select({
      table: 'proxyAuth',
      prototype: {
        proxyAddress: keystore.address,
        operationKey: operations.$login,
      },
      order: 'expireDate',
      desc: true,
      limit: 1,
    });
    const row = await cursor.getFirst();

    /* found a $login proxy key */
    if (row)
    {
      this.#authAddress = row.authAddress;
      this.pxRow = row;
      return;
    }

    /* restored context was originally from a $login proxy, but that key is gone now */
    if (this.pxRow)
    {
      this.destroy();
      throw new DCPError('proxy keystore no longer exists', 'ELOGOUT');
    }

    /* must be an unproxied key */
    this.#authAddress = keystore.address;
  }

  /**
   * Restore previously-populated AuthContext from the d9ve origin; get the keystore from localStorage
   * and use it to populate the object. Other properties of 'this' can tag along.
   */
  async restore()
  {
    const authAddress = false
          || sessionStorage.getItem('cid-ptr-' + this.#identifier)
          || localStorage  .getItem('cid-ptr-' + this.#identifier);
    if (!authAddress)
      throw new Error(`no context with identifier ${this.#identifier} stored on this origin`);
    else
      this.#authAddress = new Address(authAddress);

    const contextSnapshot = false
          || sessionStorage.getItem('context-' + this.#authAddress)
          || localStorage  .getItem('context-' + this.#authAddress);
    if (contextSnapshot)
    {
      const persist = this.persist;
      const snapshot = KVIN.parse(contextSnapshot);
      this.persist = (persist || this.persist) !== false ? true : this.persist; /* persist is sticky */
      const keystore = await snapshot.keystore; /* KVIN bug deserializes async ctor as promise, DCP-4423 should help */
      await this.populate(keystore, snapshot);
    }
  }

  /**
   * Save this AuthContext to local storage, for later retrieval by restore().
   */
  save()
  {
    const storageHnd = this.persist ? localStorage : sessionStorage;
    storageHnd.setItem('cid-ptr-' + this.#identifier, this.#authAddress);
    storageHnd.setItem('context-' + this.#authAddress, KVIN.stringify(this));
  }

  destroy()
  {
    localStorage.removeItem('cid-ptr-' + this.#identifier);
    localStorage.removeItem('context-' + this.#authAddress);
    sessionStorage.removeItem('cid-ptr-' + this.#identifier);
    sessionStorage.removeItem('context-' + this.#authAddress);
    this.#authAddress = undefined;
  }
}
